import React from "react";

import type { ReportRequestedType, ResearchAngle } from "../lib/report";

const REPORT_TYPE_LABEL: Record<ReportRequestedType, string> = {
  business: "ビジネス",
  general: "一般",
};

type ReportPlanEditorProps = {
  interpretation: string;
  reportType: ReportRequestedType;
  angles: ResearchAngle[];
  warnings: string[];
  onChangeAngles: (angles: ResearchAngle[]) => void;
  disabled?: boolean;
};

export function ReportPlanEditor({
  interpretation,
  reportType,
  angles,
  warnings,
  onChangeAngles,
  disabled = false,
}: ReportPlanEditorProps) {
  const selectedCount = angles.filter((angle) => angle.selected).length;

  const toggleSelected = (index: number) => {
    onChangeAngles(angles.map((angle, i) => (i === index ? { ...angle, selected: !angle.selected } : angle)));
  };

  const changeHeading = (index: number, heading: string) => {
    onChangeAngles(angles.map((angle, i) => (i === index ? { ...angle, heading } : angle)));
  };

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= angles.length) return;
    const next = angles.slice();
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    onChangeAngles(next);
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs font-black tracking-[0.12em] text-[#A67C52]">調査計画</p>
        <p className="mt-2 text-sm leading-6 text-[#5D4D46]">{interpretation}</p>
        <p className="mt-1 text-xs font-bold text-[#8A746A]">レポート種類: {REPORT_TYPE_LABEL[reportType]}</p>
      </div>

      {warnings.length > 0 && (
        <ul className="space-y-1 rounded-2xl bg-[#FFF7EA] p-3 text-xs font-bold text-[#9C7A2E]">
          {warnings.map((warning, index) => (
            <li key={index}>{warning}</li>
          ))}
        </ul>
      )}

      <ul className="space-y-3">
        {angles.map((angle, index) => (
          <li
            key={angle.id}
            className="rounded-2xl border border-[#EADBC8] bg-white/90 p-4 shadow-[0_10px_28px_rgba(124,90,78,0.08)]"
          >
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={angle.selected}
                onChange={() => toggleSelected(index)}
                disabled={disabled}
                aria-label={`${angle.heading || "論点"}を選択`}
                className="mt-1 h-5 w-5 shrink-0 rounded border-[#D8C0AF] text-[#7C5A4E] focus:ring-[#A67C52]"
              />
              <div className="min-w-0 flex-1">
                <input
                  type="text"
                  value={angle.heading}
                  onChange={(event) => changeHeading(index, event.target.value)}
                  disabled={disabled}
                  maxLength={100}
                  aria-label="論点の見出し"
                  className="w-full rounded-xl border border-transparent bg-transparent px-1 py-1 text-sm font-black text-[#4A3E39] outline-none transition focus:border-[#A67C52] focus:bg-white"
                />
                {angle.why && <p className="mt-1 text-xs leading-5 text-[#8A746A]">なぜ調べるか: {angle.why}</p>}
              </div>
              <div className="flex shrink-0 flex-col gap-1">
                <button
                  type="button"
                  onClick={() => move(index, -1)}
                  disabled={disabled || index === 0}
                  aria-label="上へ移動"
                  className="rounded-full border border-[#D8C0AF] bg-white px-2 py-1 text-xs font-black text-[#7C5A4E] transition hover:border-[#A67C52] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => move(index, 1)}
                  disabled={disabled || index === angles.length - 1}
                  aria-label="下へ移動"
                  className="rounded-full border border-[#D8C0AF] bg-white px-2 py-1 text-xs font-black text-[#7C5A4E] transition hover:border-[#A67C52] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  ↓
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>

      <p className="text-xs font-bold text-[#8A746A]" aria-live="polite">
        選択中: {selectedCount}件(1〜4件を選択してください)
      </p>
    </div>
  );
}
